'use client'

import { useEffect, useState } from 'react'
import type { ActionItem } from '@/types/radar'
import { platformHeaders } from '@/lib/platform/client-headers'
import { deriveGscActions, gscAuditKey, type GscPayload } from './gsc-merge'

interface UseGscEnrichmentOptions {
  activeDomain: string | null
  hasCompletedResults: boolean
  auditGeneration: number
}

export function useGscEnrichment({
  activeDomain,
  hasCompletedResults,
  auditGeneration,
}: UseGscEnrichmentOptions) {
  const [payload, setPayload] = useState<GscPayload | null>(null)
  const fetchKey = gscAuditKey(activeDomain, hasCompletedResults)
  const derived = deriveGscActions(payload, fetchKey, auditGeneration)

  useEffect(() => {
    if (!fetchKey || derived.settled || !activeDomain) return

    const controller = new AbortController()
    let cancelled = false

    fetch('/api/platform/gsc-findings', {
      method: 'POST',
      headers: platformHeaders(),
      body: JSON.stringify({ domain: activeDomain }),
      signal: controller.signal,
    })
      .then(response => (response.ok ? response.json() : null))
      .then((data: { actions?: ActionItem[] } | null) => {
        if (cancelled) return
        setPayload({
          key: fetchKey,
          generation: auditGeneration,
          actions: Array.isArray(data?.actions) ? data.actions : [],
        })
      })
      .catch(error => {
        if (cancelled) return
        console.error('[GSC] Findings fetch failed:', error)
        setPayload({ key: fetchKey, generation: auditGeneration, actions: [] })
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [activeDomain, auditGeneration, derived.settled, fetchKey])

  return derived
}
